<script lang="ts">
  import type { ButtonSize, ColorScheme, IconName } from '$lib/types/components';
  import type { HTMLAnchorAttributes, HTMLButtonAttributes } from 'svelte/elements';
  import { twMerge } from 'tailwind-merge';
  import Icon from './Icon.svelte';

  type ButtonVariant =
    | 'primary'
    | 'outlinePrimary'
    | 'outlineSecondary'
    | 'naked'
    | 'link'
    | 'arrowLink';

  export let href: string | undefined = undefined;
  export let variant: ButtonVariant | undefined = 'primary';
  export let scheme: ColorScheme = 'light';
  export let size: ButtonSize = 'md';
  export let iconName: IconName | undefined = undefined;
  export let type: HTMLButtonAttributes['type'] = 'button';
  export let tabindex: HTMLButtonAttributes['tabindex'] = 0;

  const classes = {
    base: 'font-semibold text-sm leading-none transition-[color,_background-color,_border-color,_text-decoration-color,_fill,_stroke,_opacity] duration-300',
    button: 'rounded-full inline-flex items-center justify-center',
    variants: {
      primary: 'text-white bg-blue hover:bg-blue-700 disabled:bg-blue',
      outlinePrimary:
        'ring ring-inset ring-1 ring-blue text-blue hover:bg-blue hover:text-white disabled:bg-transparent disabled:text-blue space-x-6',
      outlineSecondary: {
        light:
          'ring ring-inset ring-1 ring-blue-950 text-blue-950 hover:bg-blue-950 hover:text-white disabled:bg-transparent disabled:text-blue-950',
        dark: 'ring ring-inset ring-1 ring-white text-white hover:bg-white hover:text-blue-950 disabled:bg-transparent disabled:text-white'
      },
      naked: 'font-inherit text-inherit leading-inherit',
      link: 'text-blue hover:text-blue-700',
      arrowLink: 'group text-blue hover:text-blue-700 '
    },
    sizes: {
      sm: 'px-5 py-3',
      md: 'px-6 py-4',
      lg: 'px-6 py-5'
    },
    disabled: 'cursor-not-allowed opacity-50'
  };

  const isButtonShaped =
    variant === 'primary' || variant === 'outlinePrimary' || variant === 'outlineSecondary';

  let conditionalAttributes: HTMLButtonAttributes | HTMLAnchorAttributes = {};

  if (!href) conditionalAttributes.type = type;

  const { class: restPropsClass, ...restProps } = $$restProps;
</script>

<svelte:element
  this={href ? 'a' : 'button'}
  class={twMerge(
    variant !== 'naked' && classes.base,
    isButtonShaped && classes.button,
    variant === 'primary' && classes.variants.primary,
    variant === 'outlinePrimary' && classes.variants.outlinePrimary,
    variant === 'outlineSecondary' && scheme === 'light' && classes.variants.outlineSecondary.light,
    variant === 'outlineSecondary' && scheme === 'dark' && classes.variants.outlineSecondary.dark,
    variant === 'link' && classes.variants.link,
    variant === 'arrowLink' && classes.variants.arrowLink,

    isButtonShaped && classes.sizes[size],
    $$props.disabled && classes.disabled,
    restPropsClass
  )}
  {...restProps}
  {href}
  {tabindex}
  {...conditionalAttributes}
  role="button"
  on:click
  on:change
  on:keydown
  on:keyup
  on:touchstart|passive
  on:touchend
  on:touchcancel
  on:mouseenter
  on:mouseleave
>
  {#if iconName}
    <Icon name={iconName} class="mr-2" />
  {/if}
  <slot />
  {#if variant === 'arrowLink'}
    <Icon
      name="arrow-right"
      class="duration-150 group-hover:translate-x-1 group-hover:enabled:text-blue-700"
    />
  {/if}
</svelte:element>

<!--
@component
For all things clickable. The underlying DOM element is `button` by default. Pass `href` to change
element to an anchor tag. You may pass any attribute native to \<a\> or \<button\>.
## Props
@prop variant: 'primary' |'outlinePrimary'|'outlineSecondary'|'naked'| 'link' | 'arrowLink' = 'primary';
@prop scheme: ColorScheme = 'light';
@prop size: 'sm' | 'md' | 'lg' = 'md';
 @prop iconName: IconName | undefined = undefined;
-->
